import { GetterTree } from "vuex/types/index";

import { VenueType } from "~/constants/Venues";
import { SohoModel } from "~/constants/SohoModel";
import { getRelationshipId, getResource } from "~/utils/store";
import { RootState } from "~/store/store.types";

import { VenuesState, Venue, VenueResource } from "./venues.types";

const getters: GetterTree<VenuesState, RootState> = {
  topLevelVenues (state) {
    return Object.values(state.data).filter(({ attributes: { isTopLevel } }) => isTopLevel)
  },
  
  resourceById (state, _, __, rootGetters) {
    return (id: string, included: string | string[] = []) => {
      return getResource<VenueResource>(state, id, rootGetters, included)
    }
  },
  
  venuesByType (state, { topLevelVenues }, __, rootGetters) {
    return (type: string, included: string | string[] = []) => {
      const venues = (topLevelVenues as VenueResource[]).filter(({ attributes: { venueType } }) => venueType === type)
      return venues.map(({ id }) => getResource<VenueResource>(state, id, rootGetters, included))
    }
  },

  houses (_, getters) {
    return getters.venuesByType(VenueType.House, [SohoModel.House, SohoModel.Hotel])
  },

  citiesWithoutHouses (_, getters) {
    return getters.venuesByType(VenueType.CWH)
  },

  venueBySlug (state, _, __, rootGetters) {
    return (venueBySlug: string) => {
      const venue = Object.values(state.data).find(({ attributes: { slug } }) => slug === venueBySlug)

      if (venue) {
        return getResource<VenueResource>(state, venue.id, rootGetters)
      }

      return undefined
    }
  },

  venuesWithRooms: (state, _, __, rootGetters) => {
    const venues = Object.values(state.data).filter(({ relationships }) => getRelationshipId(SohoModel.Hotel, relationships))

    return venues.map(({ id }) => getResource<VenueResource>(state, id, rootGetters, [SohoModel.Hotel]))
  },

  venuesByHotelId: (state, _, __, rootGetters) => {
    return (id: string) => {
      const venues = Object.values(state.data).filter(({ relationships }) => getRelationshipId(SohoModel.Hotel, relationships) === id)
      return venues.map(({ id }) => getResource<VenueResource>(state, id, rootGetters))
    }
  },

  activeVenues: (_, getters) =>
    getters.topLevelVenues.filter(({ relationships }: VenueResource) => getRelationshipId('activeParentVenue', relationships)).map(({ id }: Venue) => id),
  
  activeVenuesByParentId (_, getters) {
    return (id: string) => getters.topLevelVenues.reduce((activeVenuesIds: string[], { id: activeId, relationships }: VenueResource) => {
      const relationshipId = getRelationshipId('activeParentVenue', relationships)
      if (relationshipId === id) {
        return activeVenuesIds.concat([activeId])
      }

      return activeVenuesIds
    }, [])
  }
}

export default getters
